import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { InitialSetupStatus, InitialSetupValues, SetupEmailTestRequest } from './initial-setup.model';
import { GlobalVariablesService } from '@app/core/services/global-variables.service';

@Injectable({
    providedIn: 'root'
})
export class InitialSetupService {

    private statusSubject = new ReplaySubject<InitialSetupStatus>(1);
    private currentStatus?: InitialSetupStatus;
    private inflight?: Promise<InitialSetupStatus>;

    constructor(
        private http: HttpClient,
        private globals: GlobalVariablesService
    ) { }

    get status$() {
        return this.statusSubject.asObservable();
    }

    async fetchStatus(force = false): Promise<InitialSetupStatus> {
        if (!force && this.currentStatus) {
            return this.currentStatus;
        }

        if (!this.inflight) {
            this.inflight = firstValueFrom(
                this.http
                    .get(`${environment.apiv2}/setup/status`, {
                        headers: this.buildHeaders(),
                        responseType: 'text'
                    })
                    .pipe(map((body: string) => this.parseStatus(body)))
            )
                .then((status) => this.persistStatus(status))
                .catch((error) => {
                    if (this.isSetupLockedResponse(error)) {
                        return this.persistStatus(this.createLockedStatus());
                    }
                    throw error;
                })
                .finally(() => {
                    this.inflight = undefined;
                });
        }

        return this.inflight;
    }

    async completeSetup(payload: InitialSetupValues): Promise<InitialSetupStatus> {
        const status = await firstValueFrom(
            this.http.post<InitialSetupStatus>(`${environment.apiv2}/setup`, payload, {
                headers: this.buildHeaders()
            })
        );
        return this.persistStatus(status);
    }

    async testEmailSettings(payload: SetupEmailTestRequest): Promise<void> {
        await firstValueFrom(
            this.http.post(`${environment.apiv2}/setup/test-email`, payload, {
                headers: this.buildHeaders()
            })
        );
    }

    private parseStatus(raw: string): InitialSetupStatus {
        if (!raw) {
            return this.createDefaultStatus();
        }

        try {
            const parsed = JSON.parse(raw);
            return this.normalizeStatus(parsed);
        } catch (error) {
            console.error('Failed to parse setup status payload', error, raw);
            return this.createDefaultStatus();
        }
    }

    private normalizeStatus(payload: any): InitialSetupStatus {
        if (!payload) {
            return this.createDefaultStatus();
        }

        const values = payload.values ?? payload.Values ?? {};

        return {
            isConfigured: this.coalesce(payload.isConfigured, payload.IsConfigured, false),
            hasSmtpPassword: this.coalesce(payload.hasSmtpPassword, payload.HasSmtpPassword, false),
            missingFields: this.coalesce(payload.missingFields, payload.MissingFields, []),
            values: {
                customerName: this.coalesce(values.customerName, values.CustomerName, ''),
                websiteAddress: this.coalesce(values.websiteAddress, values.WebsiteAddress, ''),
                photoServerAddress: this.coalesce(values.photoServerAddress, values.PhotoServerAddress, ''),
                trackerSignalRAddress: this.coalesce(values.trackerSignalRAddress, values.TrackerSignalRAddress, ''),
                timeZone: this.coalesce(values.timeZone, values.TimeZone, 'Europe/Istanbul'),
                emailServer: this.coalesce(values.emailServer, values.EmailServer, ''),
                emailPort: this.coalesce(values.emailPort, values.EmailPort, null),
                enableSsl: this.coalesce(values.enableSsl, values.EnableSsl, true),
                emailUsername: this.coalesce(values.emailUsername, values.EmailUsername, ''),
                emailPassword: this.coalesce(values.emailPassword, values.EmailPassword, ''),
                emailSender: this.coalesce(values.emailSender, values.EmailSender, ''),
                replyTo: this.coalesce(values.replyTo, values.ReplyTo, '')
            }
        };
    }

    private coalesce<T>(...values: Array<T | undefined>): T {
        for (const value of values) {
            if (value !== undefined && value !== null) {
                return value;
            }
        }
        return values[values.length - 1] as T;
    }

    private createDefaultStatus(): InitialSetupStatus {
        return {
            isConfigured: false,
            hasSmtpPassword: false,
            missingFields: [],
            values: {
                customerName: '',
                websiteAddress: '',
                photoServerAddress: '',
                trackerSignalRAddress: '',
                timeZone: 'Europe/Istanbul',
                emailServer: '',
                emailPort: null,
                enableSsl: true,
                emailUsername: '',
                emailPassword: '',
                emailSender: '',
                replyTo: ''
            }
        };
    }

    private createLockedStatus(): InitialSetupStatus {
        const base = this.createDefaultStatus();
        return {
            ...base,
            isConfigured: true
        };
    }

    private isSetupLockedResponse(error: any): boolean {
        return error instanceof HttpErrorResponse && error.status === 403;
    }

    private buildHeaders(): HttpHeaders | undefined {
        const tenantId = this.globals.app?.tenantId;
        if (tenantId) {
            return new HttpHeaders({
                TenantId: tenantId,
                Accept: 'application/json'
            });
        }
        return undefined;
    }

    private persistStatus(status: InitialSetupStatus): InitialSetupStatus {
        this.currentStatus = status;
        this.statusSubject.next(status);
        return status;
    }
}
